/*
 * Frame-to-frame two-way communication (via postMessage).
 *
 * Security & integrity checks included: Check whether the message originates
 * from the same origin and from a specific window (the same that we send
 * messages to).
 *
 * Fix: We check both the target and source frame, thus namespaces
 * are not longer needed to prevent message clashes.
 */
import debugFactory from 'debug';

const debug = debugFactory('ingrid:ipc');

interface IpcProps {
  origin: string;
  window: Window;
}

type IpcListener<TPayload> = (payload: TPayload) => void;
type IpcListenerCleaner = () => void;

export interface IpcInstance<TPayload> {
  send: (payload: TPayload) => void;
  listen: (callback: IpcListener<TPayload>) => IpcListenerCleaner;
  detach: () => void;
}

/**
 * Wraps over postMessage API to only communicate on given window and origin
 * @param {string} window - Window object for the IPC to operate on
 * @param {string} origin - Origin you only want to accept the events from
 */
export function createIpc<TPayload>(props: IpcProps): IpcInstance<TPayload> {
  const listeners: IpcListener<TPayload>[] = [];
  let attached = false;

  function receiveMessage(event: MessageEvent) {
    if (event.origin !== props.origin) {
      // Security check: origin does not match.
      return;
    }

    if (event.source !== props.window) {
      // Integrity check: communication from different frame.
      return;
    }

    if (event.data === null || typeof event.data !== 'object') {
      // Data check: data is not an object.
      return;
    }

    if (event.data.signature !== 'ingrid' || event.data.payload === undefined) {
      // Integrity check: we still might get messages from other applications here.
      return;
    }

    debug(`Received message: ${JSON.stringify(event.data.payload)}`);

    listeners.forEach(listener => {
      listener(event.data.payload);
    });
  }

  function send(payload: TPayload) {
    const data = {
      payload,
      signature: 'ingrid',
    };

    debug(`Sending message: ${JSON.stringify(payload)}`);

    props.window.postMessage(data, props.origin);
  }

  function listen(callback: (payload: TPayload) => void) {
    if (!attached) {
      window.addEventListener('message', receiveMessage);
      attached = true;
    }
    listeners.push(callback);

    return () => {
      // remove listener
      const idx = listeners.findIndex(listener => listener === callback);
      if (idx >= 0) {
        listeners.splice(idx, 1);
      }
    };
  }

  function detach() {
    if (attached) {
      window.removeEventListener('message', receiveMessage);
      attached = false;
    }
  }

  return {
    send,
    listen,
    detach,
  };
}
